import { useCallback, useEffect, useMemo, useState } from 'react';
import useAuthStore from '../store/authStore';
import { usePermissions } from './usePermissions';
import { useTenant } from '../contexts/TenantContext';
import { fetchWithTenant } from '../utils/apiClient';
import { isPublicAuthRoute } from '../utils/routeUtils';
import {
  buildOnboardingSteps,
  clearOnboardingStepsCache,
  getOnboardingPrefs,
  isOnboardingComplete,
  isOnboardingEligible,
} from '../utils/onboardingStatus';

const defaultPrefs = {
  completed: false,
  skipped: false,
  skippedUntil: null,
};

const buildFocusedStepStorageKey = (userId, tenantId) =>
  `onboarding-focused-step:${userId || 'none'}:${tenantId || 'none'}`;

export function useOnboardingSteps(options = {}) {
  const {
    enabled = true,
    pathname = '',
    skipPublicAuthRoutes = false,
    autoRefreshMs = 0,
    refreshOnFocus = false,
  } = options;

  const { user, hasHydrated, isAuthenticated } = useAuthStore();
  const { tenant } = useTenant();
  const {
    canUpdateTenantConfig,
    canUpdateJobFilters,
    canReadVectorData,
    canInviteUser,
    canReadProposal,
  } = usePermissions();

  const [loading, setLoading] = useState(true);
  const [steps, setSteps] = useState([]);
  const [flowVariant, setFlowVariant] = useState('solo');
  const [focusedStepId, setFocusedStepIdState] = useState(null);

  const isEligible = isAuthenticated && isOnboardingEligible(user);
  const isPublicAuthPath = skipPublicAuthRoutes && isPublicAuthRoute(pathname);

  const canUpdateTenant = canUpdateTenantConfig();
  const canUpdateFilters = canUpdateJobFilters();
  const canReadKnowledge = canReadVectorData();
  const canInvite = canInviteUser();
  const canReadProposalData = canReadProposal();

  const permissions = useMemo(
    () => ({
      canUpdateTenant,
      canUpdateFilters,
      canReadKnowledge,
      canInvite,
      canReadProposalData,
    }),
    [canUpdateTenant, canUpdateFilters, canReadKnowledge, canInvite, canReadProposalData]
  );

  const canLoadSteps = enabled && hasHydrated && isEligible && !isPublicAuthPath;
  const focusedStepStorageKey = useMemo(() => {
    if (!user?._id) return null;
    return buildFocusedStepStorageKey(user._id, tenant?._id);
  }, [user?._id, tenant?._id]);

  const setFocusedStepId = useCallback((stepId) => {
    const normalizedStepId = typeof stepId === 'string' && stepId.trim() ? stepId.trim() : null;
    setFocusedStepIdState(normalizedStepId);

    if (!focusedStepStorageKey) return;
    try {
      if (normalizedStepId) {
        sessionStorage.setItem(focusedStepStorageKey, normalizedStepId);
      } else {
        sessionStorage.removeItem(focusedStepStorageKey);
      }
    } catch {
      // Ignore storage access errors (private mode, quota limits)
    }
  }, [focusedStepStorageKey]);

  useEffect(() => {
    if (!focusedStepStorageKey) {
      setFocusedStepIdState(null);
      return;
    }

    try {
      const stored = sessionStorage.getItem(focusedStepStorageKey);
      setFocusedStepIdState(stored || null);
    } catch {
      setFocusedStepIdState(null);
    }
  }, [focusedStepStorageKey]);

  const refreshSteps = useCallback(async (optionsOverride = {}) => {
    const {
      showLoading = false,
      forceRefresh = true,
    } = optionsOverride;

    if (!canLoadSteps) {
      setLoading(false);
      setSteps([]);
      return [];
    }

    if (showLoading) {
      setLoading(true);
    }

    try {
      if (forceRefresh) {
        clearOnboardingStepsCache();
      }

      const result = await buildOnboardingSteps(fetchWithTenant, user, permissions, {
        tenantId: tenant?._id,
        forceRefresh,
      });

      // Handle both old format (array) and new format (object with steps and flowVariant)
      const stepsData = result?.steps || result;
      const variant = result?.flowVariant || 'solo';
      
      const normalized = Array.isArray(stepsData) ? stepsData : [];
      setSteps(normalized);
      setFlowVariant(variant);
      return normalized;
    } catch {
      setSteps([]);
      return [];
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [canLoadSteps, user, permissions, tenant?._id]);

  useEffect(() => {
    let cancelled = false;

    const loadSteps = async () => {
      if (!canLoadSteps) {
        setLoading(false);
        setSteps([]);
        return;
      }

      if (steps.length === 0) {
        setLoading(true);
      }

      try {
        const result = await buildOnboardingSteps(fetchWithTenant, user, permissions, {
          tenantId: tenant?._id,
        });
        if (!cancelled) {
          // Handle both old format (array) and new format (object with steps and flowVariant)
          const stepsData = result?.steps || result;
          const variant = result?.flowVariant || 'solo';
          
          setSteps(Array.isArray(stepsData) ? stepsData : []);
          setFlowVariant(variant);
        }
      } catch {
        if (!cancelled) {
          setSteps([]);
        }
      } finally {
        if (!cancelled && steps.length === 0) {
          setLoading(false);
        }
      }
    };

    loadSteps();

    return () => {
      cancelled = true;
    };
  }, [
    canLoadSteps,
    pathname,
    permissions,
    user?._id,
    user?.role,
    user?.email,
    tenant?._id,
  ]);

  useEffect(() => {
    if (!canLoadSteps || autoRefreshMs <= 0) return undefined;

    const interval = setInterval(() => {
      refreshSteps({ showLoading: false, forceRefresh: true });
    }, autoRefreshMs);

    return () => clearInterval(interval);
  }, [canLoadSteps, autoRefreshMs, refreshSteps]);

  useEffect(() => {
    if (!canLoadSteps || !refreshOnFocus) return undefined;

    const handleFocus = () => {
      refreshSteps({ showLoading: false, forceRefresh: true });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        handleFocus();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [canLoadSteps, refreshOnFocus, refreshSteps]);

  const onboardingPrefs = useMemo(() => {
    if (!user?._id) return { ...defaultPrefs };
    return getOnboardingPrefs(user?._id, tenant?._id);
  }, [user?._id, tenant?._id]);

  const completedCount = useMemo(
    () => steps.filter((step) => step.completed).length,
    [steps]
  );
  const allCompleted = steps.length > 0 && completedCount === steps.length;
  const onboardingIsComplete = isOnboardingComplete(steps, onboardingPrefs);

  return {
    loading,
    steps,
    flowVariant,
    isEligible,
    isPublicAuthPath,
    onboardingPrefs,
    onboardingIsComplete,
    completedCount,
    allCompleted,
    hasHydrated,
    user,
    tenant,
    refreshSteps,
    focusedStepId,
    setFocusedStepId,
  };
}

export default useOnboardingSteps;
